import React, { FC, useCallback, useEffect, useState } from 'react';
import { getIn, useFormikContext } from 'formik';
import { parse, differenceInYears } from 'date-fns';
import { Checkbox } from '@clearcover/atlas';

import { Row, Column, FormInput, TextLinkParser } from 'Shared/components';
import { GetConfigResponseType } from 'QuoteApp/services/endpoints/GetConfig';
import {
  DriverFieldsType,
  FormValuesType,
} from 'QuoteApp/services/endpoints/GetDetails';
import {
  getFieldErrors,
  maskedMonthDateYearNormalizer,
  monthDateYearNormalizer,
} from 'QuoteApp/utils';
import { AccidentList, ViolationList } from 'QuoteApp/components/IncidentList';
import { useEdit, useFieldData } from 'QuoteApp/utils/contexts';
import { useAppContext } from 'QuoteApp/services/providers';
import { FormErrorBanner } from 'QuoteApp/components/FormErrorBanner';
import { ClientFieldDataType } from 'QuoteApp/services/endpoints/GetDetails';
import { STATE_OPTIONS } from 'Shared/constants/states';
import { CASE_NUMBER_LOOKUP_URL } from 'Shared/constants/common';

import styles from './styles.module.css';
import { useQuoteAppFeatureFlags } from 'QuoteApp/utils/hooks';
import { findByMatchingProperties, stringToBoolean } from 'Shared/utils';

const calculateAge = (dob: string) => {
  const date = parse(dob, 'MM/dd/yyyy', new Date());
  const age = differenceInYears(new Date(), date);
  return age;
};

const SINGLE_STATUSES = ['single', 'divorced', 'widowed'];

const LISTED_DRIVER_STATUS_MAP = [
  {
    livesInHousehold: true,
    hasAccessToVehicles: true,
    hasOwnInsurancePolicy: false,
    licenseStatus: false,
    hasLearnersPermit: true,
    hasForeignLicense: false,
  },
  {
    livesInHousehold: true,
    hasAccessToVehicles: false,
    hasOwnInsurancePolicy: true,
    licenseStatus: true,
    hasLearnersPermit: false,
    hasForeignLicense: false,
  },
  {
    livesInHousehold: true,
    hasAccessToVehicles: false,
    hasOwnInsurancePolicy: true,
    licenseStatus: false,
    hasLearnersPermit: false,
    hasForeignLicense: true,
  },
  {
    livesInHousehold: true,
    hasAccessToVehicles: false,
    hasOwnInsurancePolicy: false,
    licenseStatus: false,
    hasLearnersPermit: true,
    hasForeignLicense: false,
  },
  {
    livesInHousehold: true,
    hasAccessToVehicles: false,
    hasOwnInsurancePolicy: false,
    licenseStatus: false,
    hasLearnersPermit: false,
    hasForeignLicense: false,
  },
  {
    livesInHousehold: false,
    hasAccessToVehicles: true,
    hasOwnInsurancePolicy: false,
    licenseStatus: false,
    hasLearnersPermit: true,
    hasForeignLicense: false,
  },
];

const shouldDisplayGoodStudentQuestion = (
  goodStudentConfig: GetConfigResponseType['drivers']['goodStudentDiscount'],
  driver: DriverFieldsType,
  dirty: boolean,
  inputValue: string,
  serverErrors?: ClientFieldDataType['errors']
) => {
  // return early if not enabled
  if (!goodStudentConfig || !goodStudentConfig.enabled) {
    return false;
  }

  // bypass validation if there already is a saved value or errors from the server and you are not editing
  if (inputValue || (!dirty && serverErrors && serverErrors.length > 0)) {
    return true;
  }

  const hasValidStudentAge =
    typeof goodStudentConfig.mustBeYoungerThan === 'number' &&
    calculateAge(driver.dateOfBirth) < goodStudentConfig.mustBeYoungerThan;

  // only use single statuses if config has mustBeUnmarried
  const hasValidStudentMaritalStatus = goodStudentConfig.mustBeUnmarried
    ? SINGLE_STATUSES.includes(driver.maritalStatus)
    : true;

  return hasValidStudentAge && hasValidStudentMaritalStatus;
};

type DriverFormPropsType = {
  driverId: string;
  driverType: DriverFieldsType['driverType'];
  config: GetConfigResponseType['drivers'];
};

export const DriverForm: FC<DriverFormPropsType> = ({
  driverId,
  driverType,
  config,
}) => {
  const {
    setFieldValue,
    handleChange,
    handleBlur,
    touched,
    errors,
    values,
    initialValues,
    dirty,
  } = useFormikContext<FormValuesType>();

  const { details, config: appConfig } = useAppContext();
  const { editingId } = useEdit();
  const { serverErrors, alwaysShowErrors, editableFields } = useFieldData();
  const { undisclosedDriversFlag } = useQuoteAppFeatureFlags();
  const readOnly = editingId !== driverId;
  const [markedAsListedDriver, setMarkedAsListedDriver] = useState<boolean>(
    false
  );

  const applicationState =
    details.address[details.principalInsuredId].state.value;
  const currentDriver = values.drivers[driverId];

  const getFullFieldName = useCallback(
    (fieldName: keyof Omit<DriverFieldsType, 'accidents' | 'violations'>) =>
      `drivers.${driverId}.${fieldName}`,
    [driverId]
  );

  const getInputProps = useCallback(
    (
      fieldName: keyof Omit<
        DriverFieldsType,
        | 'editable'
        | 'accidents'
        | 'violations'
        | 'discoveredDriver'
        | 'declinedSsn'
      >,
      required = true
    ) => {
      const fullFieldName = getFullFieldName(fieldName);
      return {
        name: fullFieldName,
        onChange: handleChange,
        onBlur: handleBlur,
        value: currentDriver[fieldName],
        disabled: readOnly || !getIn(editableFields, fullFieldName),
        errorMessage: editableFields.drivers?.[driverId][fieldName]
          ? getFieldErrors<FormValuesType>(
              fullFieldName,
              required,
              alwaysShowErrors,
              errors,
              serverErrors,
              values,
              touched,
              initialValues
            )
          : undefined,
      };
    },
    [
      getFullFieldName,
      handleChange,
      handleBlur,
      currentDriver,
      readOnly,
      editableFields,
      driverId,
      alwaysShowErrors,
      errors,
      serverErrors,
      values,
      touched,
      initialValues,
    ]
  );

  const isPrimaryDriver = currentDriver.driverType === 'principalInsured';
  const isExludedDriver = currentDriver.excludeFromQuote === 'true';
  const licenseStatus = currentDriver.licenseStatus;
  const currentInsuranceBiLevel = currentDriver.currentInsuranceBiLevel;

  useEffect(() => {
    const checkIfIsListedDriver = async () => {
      if (isPrimaryDriver || isExludedDriver) {
        if (markedAsListedDriver) setMarkedAsListedDriver(false);
        return;
      }

      const currentStatus = {
        livesInHousehold: stringToBoolean(currentDriver.livesInHousehold),
        hasAccessToVehicles: stringToBoolean(currentDriver.hasAccessToVehicles),
        hasOwnInsurancePolicy: stringToBoolean(
          currentDriver.hasOwnInsurancePolicy
        ),
        licenseStatus: licenseStatus === 'active',
        hasLearnersPermit: stringToBoolean(currentDriver.hasLearnersPermit),
        hasForeignLicense: stringToBoolean(currentDriver.hasForeignLicense),
      };

      const matchFound = (
        await findByMatchingProperties(LISTED_DRIVER_STATUS_MAP, currentStatus)
      ).length;

      setMarkedAsListedDriver(!!matchFound);
    };

    checkIfIsListedDriver();
  }, [
    currentDriver,
    isExludedDriver,
    isPrimaryDriver,
    licenseStatus,
    markedAsListedDriver,
  ]);

  useEffect(() => {
    if (readOnly || !config.cfrs?.defaultIssueState) return;

    const field = getFullFieldName('cfrIssueState');

    if (currentDriver.cfrRequired === 'true' && !currentDriver.cfrIssueState) {
      const defaultState =
        initialValues.drivers[driverId].cfrIssueState ||
        config.cfrs.defaultIssueState;
      setFieldValue(field, defaultState);
    } else if (
      currentDriver.cfrRequired === 'false' &&
      currentDriver.cfrIssueState
    ) {
      setFieldValue(field, null);
    }
  }, [
    readOnly,
    driverId,
    setFieldValue,
    getFullFieldName,
    initialValues.drivers, // added
    currentDriver.cfrRequired,
    currentDriver.cfrIssueState,
    config.cfrs?.defaultIssueState,
  ]);

  const militaryInputProps = getInputProps('militaryServiceType');
  const isActiveMilitary = [
    'full_time_active_duty',
    'active_national_guard_or_reserve',
  ].includes(militaryInputProps.value);
  const hasServedInMilitary =
    isActiveMilitary || militaryInputProps.value === 'discharged_or_retired';

  const goodStudentInputProps = getInputProps('isGoodStudent');
  const displayGoodStudentDiscount = shouldDisplayGoodStudentQuestion(
    config.goodStudentDiscount,
    currentDriver,
    dirty,
    goodStudentInputProps.value,
    serverErrors?.drivers?.[driverId].isGoodStudent
  );

  const inStateResidencyDuration = currentDriver.inStateResidencyDuration;
  const outOfStateStudent = currentDriver.outOfStateStudent;
  const state = details.address[details.principalInsuredId].state.value;
  const hasLearnersPermit = currentDriver.hasLearnersPermit;

  const hasForeignLicenseRequired =
    config?.driverLicense?.hasForeignLicense?.required ?? false;
  const hasLearnersPermitRequired =
    config?.driverLicense?.hasLearnersPermit?.required ?? false;
  const isStateResidencyRequired = config?.stateResidency?.required ?? false;

  const {
    askForUserReportedStatus,
    askForAgeFirstLicensed,
    askForSuspensions,
  } = config.driverLicense;

  const { askForCurrentPolicy } = config.currentInsurance;
  const { askEducation } = config.education;
  const { askProfession } = config;
  const { askMilitaryService } = config.militaryService;

  return (
    <>
      {driverId === details.principalInsuredId &&
      serverErrors?.drivers?.[driverId].relationshipToApplicant.length ? (
        <FormErrorBanner message="The primary driver is marked as having a partner, but a partner is not included in the list of drivers." />
      ) : null}
      {undisclosedDriversFlag &&
        driverType !== 'principalInsured' &&
        config.allowExcludedDrivers && (
          <Row>
            <Column>
              <Checkbox
                {...getInputProps('excludeFromQuote')}
                value={currentDriver.excludeFromQuote === 'true'}
                label="Exclude driver from policy"
                onChange={value => {
                  setFieldValue(
                    getFullFieldName('excludeFromQuote'),
                    value.toString()
                  );

                  //  clean up values for hidden fields
                  if (value) {
                    setFieldValue(getFullFieldName('livesInHousehold'), '');
                    setFieldValue(getFullFieldName('hasAccessToVehicles'), '');
                    setFieldValue(
                      getFullFieldName('hasOwnInsurancePolicy'),
                      ''
                    );
                    setFieldValue(
                      getFullFieldName('inStateResidencyDuration'),
                      ''
                    );
                    setFieldValue(getFullFieldName('outOfStateStudent'), '');
                    setFieldValue(
                      getFullFieldName('ownVehicleWhileOutOfStateStudent'),
                      ''
                    );
                  }
                }}
              />
            </Column>
          </Row>
        )}
      <Row>
        <Column variant="third">
          <FormInput
            {...getInputProps('firstName')}
            variant={readOnly ? 'readOnly' : 'text'}
            label="First Name"
            data-testid="first-name"
          />
        </Column>
        <Column variant="sixth">
          <FormInput
            {...getInputProps('middleName', false)}
            variant={readOnly ? 'readOnly' : 'text'}
            label="MI"
            data-testid="middle-initial"
          />
        </Column>
        <Column variant="third">
          <FormInput
            {...getInputProps('lastName')}
            variant={readOnly ? 'readOnly' : 'text'}
            label="Last Name"
            data-testid="last-name"
          />
        </Column>
        <Column variant="sixth">
          <FormInput
            {...getInputProps('suffix', false)}
            variant={readOnly ? 'readOnly' : 'selectAutocomplete'}
            data-testid="suffix"
            onChange={(value: string) =>
              setFieldValue(getFullFieldName('suffix'), value)
            }
            label="Suffix"
            options={config.personalInfo.suffix}
          />
        </Column>
      </Row>
      <Row>
        <Column variant="third">
          <FormInput
            {...getInputProps('dateOfBirth')}
            variant={readOnly ? 'readOnly' : 'lockedText'}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setFieldValue(
                getFullFieldName('dateOfBirth'),
                maskedMonthDateYearNormalizer(e.currentTarget.value)
              );
            }}
            placeholder="MM/DD/YYYY"
            label="Date of Birth"
            data-testid="date-of-birth"
            onEdit={() => setFieldValue(getFullFieldName('dateOfBirth'), '')}
          />
        </Column>
        {currentDriver.excludeFromQuote !== 'true' ? (
          <>
            {licenseStatus !== 'unknown' && askForAgeFirstLicensed && (
              <Column variant="sixth">
                <FormInput
                  {...getInputProps(
                    'ageFirstLicensed',
                    licenseStatus !== 'unknown' || hasLearnersPermit !== 'false'
                  )}
                  variant={readOnly ? 'readOnly' : 'text'}
                  label="Age first licensed"
                  data-testid="age-first-licensed"
                />
              </Column>
            )}
            <Column />
          </>
        ) : (
          <>
            <Column variant="third">
              <FormInput
                {...getInputProps('maritalStatus')}
                variant={readOnly ? 'readOnly' : 'selectAutocomplete'}
                data-testid="marital-status"
                label="Marital Status"
                onChange={(value: string) =>
                  setFieldValue(getFullFieldName('maritalStatus'), value)
                }
                options={config.personalInfo.maritalStatus}
              />
            </Column>
            <Column variant="third">
              <FormInput
                {...getInputProps('relationshipToApplicant')}
                variant={readOnly ? 'readOnly' : 'selectAutocomplete'}
                data-testid="relationship-to-applicant"
                label="Relationship to applicant"
                onChange={(value: string) =>
                  setFieldValue(
                    getFullFieldName('relationshipToApplicant'),
                    value
                  )
                }
                options={config.personalInfo.relationshipToApplicant}
              />
            </Column>
          </>
        )}
      </Row>
      {currentDriver.excludeFromQuote === 'true' && (
        <Row>
          {undisclosedDriversFlag ? (
            <Column variant="third">
              <FormInput
                {...getInputProps(
                  'exclusionReason',
                  !!serverErrors.drivers?.[driverId]?.exclusionReason?.length
                )}
                variant={readOnly ? 'readOnly' : 'selectAutocomplete'}
                data-testid="exclusion-reason"
                label="Reason for excluding this driver"
                onChange={(value: string) =>
                  setFieldValue(getFullFieldName('exclusionReason'), value)
                }
                options={config.exclusionReason}
              />
            </Column>
          ) : (
            <Column />
          )}
        </Row>
      )}
      {currentDriver.excludeFromQuote !== 'true' && (
        <>
          <Row>
            <Column variant="third">
              <FormInput
                {...getInputProps('gender')}
                variant={readOnly ? 'readOnly' : 'selectAutocomplete'}
                data-testid="gender"
                label="Gender"
                onChange={(value: string) =>
                  setFieldValue(getFullFieldName('gender'), value)
                }
                options={config.personalInfo.gender}
              />
            </Column>
            <Column variant="third">
              <FormInput
                {...getInputProps('maritalStatus')}
                variant={readOnly ? 'readOnly' : 'selectAutocomplete'}
                data-testid="marital-status"
                label="Marital Status"
                onChange={(value: string) =>
                  setFieldValue(getFullFieldName('maritalStatus'), value)
                }
                options={config.personalInfo.maritalStatus}
              />
            </Column>
            <Column variant="third">
              {driverType !== 'principalInsured' && (
                <FormInput
                  {...getInputProps('relationshipToApplicant')}
                  variant={readOnly ? 'readOnly' : 'selectAutocomplete'}
                  data-testid="relationship-to-applicant"
                  label="Relationship to applicant"
                  onChange={(value: string) =>
                    setFieldValue(
                      getFullFieldName('relationshipToApplicant'),
                      value
                    )
                  }
                  options={config.personalInfo.relationshipToApplicant}
                />
              )}
            </Column>
          </Row>
          {appConfig?.accessEnabled && isPrimaryDriver && (
            <Row>
              <Column variant="third">
                <FormInput
                  {...getInputProps('coveredWithinLookbackPeriod')}
                  variant={readOnly ? 'readOnly' : 'radioButtonGroup'}
                  data-testid="covered-within-lookback-period"
                  label="Been insured any time in the past 5 years?"
                  onChange={(value: string) => {
                    setFieldValue(
                      getFullFieldName('coveredWithinLookbackPeriod'),
                      value
                    );
                  }}
                  options={
                    config.coverageHistory.coveredWithinLookbackPeriod.options
                  }
                />
              </Column>
            </Row>
          )}
          {driverType === 'principalInsured' && (
            <Row>
              <Column variant="third">
                <FormInput
                  {...getInputProps('isHomeOwner')}
                  variant={readOnly ? 'readOnly' : 'radioButtonGroup'}
                  data-testid="homeowner"
                  label="Homeowner?"
                  onChange={(value: string) =>
                    setFieldValue(getFullFieldName('isHomeOwner'), value)
                  }
                  options={{ true: 'Yes', false: 'No' }}
                />
              </Column>
              <Column />
            </Row>
          )}
          {isStateResidencyRequired &&
            !isPrimaryDriver &&
            currentDriver.excludeFromQuote !== 'true' && (
              <Row>
                <Column variant="third">
                  <FormInput
                    {...getInputProps('livesInHousehold')}
                    variant={readOnly ? 'readOnly' : 'radioButtonGroup'}
                    data-testid="live-in-the-household"
                    label="Driver live in the household?"
                    onChange={(value: string) =>
                      setFieldValue(getFullFieldName('livesInHousehold'), value)
                    }
                    options={{ true: 'Yes', false: 'No' }}
                  />
                </Column>
                <Column variant="third">
                  <FormInput
                    {...getInputProps('hasAccessToVehicles')}
                    variant={readOnly ? 'readOnly' : 'radioButtonGroup'}
                    data-testid="has-access-to-vehicles"
                    label="Driver regularly drives, or has regular access to, any of vehicles listed on the policy?"
                    onChange={(value: string) =>
                      setFieldValue(
                        getFullFieldName('hasAccessToVehicles'),
                        value
                      )
                    }
                    options={{ true: 'Yes', false: 'No' }}
                  />
                </Column>
                <Column variant="third">
                  <FormInput
                    {...getInputProps('hasOwnInsurancePolicy')}
                    variant={readOnly ? 'readOnly' : 'radioButtonGroup'}
                    data-testid="has-own-insurance-policy"
                    label="Own insurance policy with Clearcover or another company?"
                    onChange={(value: string) =>
                      setFieldValue(
                        getFullFieldName('hasOwnInsurancePolicy'),
                        value
                      )
                    }
                    options={{ true: 'Yes', false: 'No' }}
                  />
                </Column>
              </Row>
            )}
          <h4 className={styles.heading}>Drivers License</h4>
          <Row>
            {hasForeignLicenseRequired && (
              <Column variant="third">
                <FormInput
                  {...getInputProps(
                    'licenseStatus',
                    licenseStatus !== 'unknown'
                  )}
                  variant={readOnly ? 'readOnly' : 'radioButtonGroup'}
                  data-testid="license-status"
                  label="Active US drivers license?"
                  onChange={(value: string) => {
                    setFieldValue(getFullFieldName('licenseStatus'), value);
                    if (value === 'active') {
                      setFieldValue(getFullFieldName('hasLearnersPermit'), '');
                      setFieldValue(getFullFieldName('hasForeignLicense'), '');
                    } else if (value === 'unknown') {
                      setFieldValue(getFullFieldName('ageFirstLicensed'), '');
                    }
                  }}
                  options={{ active: 'Yes', unknown: 'No' }}
                />
              </Column>
            )}
            {askForUserReportedStatus && (
              <Column variant="third">
                <FormInput
                  {...getInputProps('userReportedStatus')}
                  variant={readOnly ? 'readOnly' : 'selectAutocomplete'}
                  data-testid="license-status"
                  label="License status"
                  onChange={(value: string) => {
                    setFieldValue(
                      getFullFieldName('userReportedStatus'),
                      value
                    );
                    if (
                      !['international', 'neverBeenLicensed'].includes(value)
                    ) {
                      if (!currentDriver.licenseState) {
                        setFieldValue(
                          getFullFieldName('licenseState'),
                          applicationState
                        );
                      }
                    } else {
                      setFieldValue(getFullFieldName('licenseState'), '');
                      setFieldValue(getFullFieldName('licenseNumber'), '');
                    }
                  }}
                  options={config.driverLicense.userReportedStatus ?? {}}
                />
              </Column>
            )}
            {(askForUserReportedStatus
              ? !['international', 'neverBeenLicensed'].includes(
                  getInputProps('userReportedStatus').value
                )
              : (hasForeignLicenseRequired && licenseStatus === 'active') ||
                !hasForeignLicenseRequired) && (
              <>
                <Column variant="third">
                  <FormInput
                    {...getInputProps('licenseState')}
                    variant={readOnly ? 'readOnly' : 'selectAutocomplete'}
                    data-testid="license-state"
                    label="Drivers License State"
                    onChange={(value: string) =>
                      setFieldValue(getFullFieldName('licenseState'), value)
                    }
                    options={config.driverLicense.state}
                  />
                </Column>
                <Column variant="third">
                  <FormInput
                    {...getInputProps('licenseNumber')}
                    variant={readOnly ? 'readOnly' : 'lockedText'}
                    data-testid="license-number"
                    label="Drivers License Number"
                    onEdit={() =>
                      setFieldValue(getFullFieldName('licenseNumber'), '')
                    }
                  />
                </Column>
              </>
            )}
          </Row>
          <Row>
            {hasLearnersPermitRequired && !isPrimaryDriver && (
              <Column variant="third">
                <FormInput
                  {...getInputProps(
                    'hasLearnersPermit',
                    licenseStatus === 'unknown'
                  )}
                  variant={readOnly ? 'readOnly' : 'radioButtonGroup'}
                  data-testid="has-learners-permit"
                  label="Valid learners permit?"
                  onChange={(value: string) => {
                    setFieldValue(getFullFieldName('hasLearnersPermit'), value);
                    if (value === 'true') {
                      setFieldValue(getFullFieldName('hasForeignLicense'), '');
                    }
                  }}
                  options={{ true: 'Yes', false: 'No' }}
                  disabled={licenseStatus !== 'unknown'}
                />
              </Column>
            )}
            {hasForeignLicenseRequired && (
              <Column variant="third">
                <FormInput
                  {...getInputProps(
                    'hasForeignLicense',
                    licenseStatus === 'unknown' &&
                      (isPrimaryDriver || hasLearnersPermit === 'false')
                  )}
                  variant={readOnly ? 'readOnly' : 'radioButtonGroup'}
                  data-testid="has-foreign-license"
                  label="Foreign driver’s license?"
                  onChange={(value: string) => {
                    setFieldValue(getFullFieldName('hasForeignLicense'), value);
                  }}
                  options={{ true: 'Yes', false: 'No' }}
                  disabled={
                    licenseStatus !== 'unknown' ||
                    (!isPrimaryDriver && hasLearnersPermit !== 'false')
                  }
                />
              </Column>
            )}
          </Row>
          {askForSuspensions && (
            <Row>
              <Column variant="third">
                <FormInput
                  {...getInputProps('licensePreviouslySuspended')}
                  variant={readOnly ? 'readOnly' : 'radioButtonGroup'}
                  data-testid="license-previously-suspended"
                  label="License previously suspended?"
                  onChange={(value: string) => {
                    setFieldValue(
                      getFullFieldName('licensePreviouslySuspended'),
                      value
                    );
                  }}
                  options={{ true: 'Yes', false: 'No' }}
                />
              </Column>
              {currentDriver.licensePreviouslySuspended === 'true' ? (
                <>
                  <Column variant="third">
                    <FormInput
                      {...getInputProps('previousSuspensionDate')}
                      variant={readOnly ? 'readOnly' : 'selectAutocomplete'}
                      data-testid="previous-suspension-date"
                      label="Most Recent Suspension Date"
                      onChange={(value: string) =>
                        setFieldValue(
                          getFullFieldName('previousSuspensionDate'),
                          value
                        )
                      }
                      options={config.drivingHistory.previousSuspensionDate}
                    />
                  </Column>
                </>
              ) : (
                <Column />
              )}
              {currentDriver.previousSuspensionDate === 'five_years_or_less' &&
              currentDriver.licensePreviouslySuspended === 'true' ? (
                <>
                  <Column variant="third">
                    <FormInput
                      {...getInputProps('previousSuspensionCount')}
                      variant={readOnly ? 'readOnly' : 'selectAutocomplete'}
                      data-testid="previous-suspension-count"
                      label="Previous Suspension Count"
                      onChange={(value: string) =>
                        setFieldValue(
                          getFullFieldName('previousSuspensionCount'),
                          value
                        )
                      }
                      options={config.drivingHistory.previousSuspensionCount}
                    />
                  </Column>
                </>
              ) : (
                <Column />
              )}
            </Row>
          )}
          <Row>
            <Column variant="third">
              <FormInput
                {...getInputProps('hasCompletedDrivingCourse')}
                variant={readOnly ? 'readOnly' : 'radioButtonGroup'}
                data-testid="driving-course-taken"
                label="Defensive driving course taken?"
                onChange={(value: string) =>
                  setFieldValue(
                    getFullFieldName('hasCompletedDrivingCourse'),
                    value
                  )
                }
                options={{ true: 'Yes', false: 'No' }}
              />
            </Column>
            <Column variant="third">
              {currentDriver.hasCompletedDrivingCourse === 'true' && (
                <FormInput
                  {...getInputProps('drivingCourseDate')}
                  variant={readOnly ? 'readOnly' : 'text'}
                  data-testid="driving-course-completion-date"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setFieldValue(
                      getFullFieldName('drivingCourseDate'),
                      monthDateYearNormalizer(e.currentTarget.value)
                    );
                  }}
                  placeholder="MM/DD/YYYY"
                  label="Completion Date"
                />
              )}
            </Column>
            <Column />
          </Row>
          {config.cfrs?.askForCfrs && (
            <>
              <div className={styles.heading}>
                <h4 className={styles.heading}>
                  SR-22 or FR-44 (Certificate of Financial Responsibility)
                </h4>
                <TextLinkParser
                  showIcon
                  text={`If the customer needs assistance looking up their case number, [click here|${CASE_NUMBER_LOOKUP_URL}]`}
                />
              </div>
              <Row>
                <Column variant="third">
                  <FormInput
                    {...getInputProps('cfrRequired')}
                    variant={readOnly ? 'readOnly' : 'radioButtonGroup'}
                    data-testid="cfr-required"
                    label="Customer has been notified to file SR-22/FR-44"
                    options={{ true: 'Yes', false: 'No' }}
                    onChange={(value: string) => {
                      setFieldValue(getFullFieldName('cfrRequired'), value);
                    }}
                  />
                </Column>
                <Column variant="third">
                  <FormInput
                    {...getInputProps(
                      'cfrType',
                      currentDriver.cfrRequired === 'true'
                    )}
                    variant={readOnly ? 'readOnly' : 'radioButtonGroup'}
                    data-testid="cfr-type"
                    label="Which certificate?"
                    options={{ SR22: 'SR-22', FR44: 'FR-44' }}
                    onChange={(value: string) => {
                      setFieldValue(getFullFieldName('cfrType'), value);
                    }}
                    disabled={currentDriver.cfrRequired !== 'true'}
                    radioGroupClassName={styles.upperCase}
                  />
                </Column>
                <Column variant="third">
                  <FormInput
                    {...getInputProps(
                      'cfrCaseNumber',
                      currentDriver.cfrRequired === 'true'
                    )}
                    variant={readOnly ? 'readOnly' : 'text'}
                    label="SR-22 or FR-44 case number"
                    data-testid="cfr-case-number"
                    disabled={currentDriver.cfrRequired !== 'true'}
                  />
                </Column>
              </Row>
              <Row>
                <Column variant="third">
                  <FormInput
                    {...getInputProps(
                      'cfrIssueState',
                      currentDriver.cfrRequired === 'true'
                    )}
                    variant={readOnly ? 'readOnly' : 'selectAutocomplete'}
                    data-testid="cfr-issue-state"
                    label="SR-22/FR-44 State Issued"
                    onChange={(value: string) =>
                      setFieldValue(getFullFieldName('cfrIssueState'), value)
                    }
                    options={STATE_OPTIONS}
                    disabled={currentDriver.cfrRequired !== 'true'}
                  />
                </Column>
              </Row>
            </>
          )}
          {!markedAsListedDriver && (askEducation || askProfession) && (
            <>
              <h4 className={styles.heading}>Education & Profession</h4>
              <Row>
                {askEducation && (
                  <Column variant="third">
                    <FormInput
                      {...getInputProps('educationLevel')}
                      variant={readOnly ? 'readOnly' : 'selectAutocomplete'}
                      data-testid="education-level"
                      label="Highest level of completed education"
                      onChange={(value: string) =>
                        setFieldValue(getFullFieldName('educationLevel'), value)
                      }
                      options={config.education.currentLevel}
                    />
                  </Column>
                )}
                {askProfession && (
                  <Column variant="third">
                    <FormInput
                      {...getInputProps('profession')}
                      variant={readOnly ? 'readOnly' : 'combobox'}
                      data-testid="profession"
                      label="Profession"
                      onChange={(value: string) =>
                        setFieldValue(getFullFieldName('profession'), value)
                      }
                      options={config.profession}
                    />
                  </Column>
                )}
                {displayGoodStudentDiscount ? (
                  <Column variant="third">
                    <FormInput
                      {...goodStudentInputProps}
                      variant={readOnly ? 'readOnly' : 'radioButtonGroup'}
                      data-testid="good-student"
                      label="Full time student with a B average or higher?"
                      onChange={(value: string) => {
                        setFieldValue(getFullFieldName('isGoodStudent'), value);
                      }}
                      options={{ true: 'Yes', false: 'No' }}
                    />
                  </Column>
                ) : (
                  <Column />
                )}
              </Row>
              {askMilitaryService && (
                <Row>
                  <Column variant="third">
                    <FormInput
                      {...militaryInputProps}
                      variant={readOnly ? 'readOnly' : 'selectAutocomplete'}
                      data-testid="military-status"
                      label="Military Status"
                      placeholder="Select a status"
                      onChange={(value: string) => {
                        setFieldValue(
                          getFullFieldName('militaryServiceType'),
                          value
                        );
                      }}
                      options={config.militaryService.serviceType}
                    />
                  </Column>
                  {hasServedInMilitary && (
                    <Column variant="third">
                      <FormInput
                        {...getInputProps('militaryRank')}
                        variant={readOnly ? 'readOnly' : 'selectAutocomplete'}
                        data-testid="military-rank"
                        label="Military Rank"
                        onChange={(value: string) =>
                          setFieldValue(getFullFieldName('militaryRank'), value)
                        }
                        options={config.militaryService.militaryRank}
                      />
                    </Column>
                  )}
                  {isActiveMilitary && (
                    <Column variant="third">
                      <FormInput
                        {...getInputProps('isDeployed')}
                        variant={readOnly ? 'readOnly' : 'radioButtonGroup'}
                        data-testid="currently-deployed"
                        label="Currently deployed overseas?"
                        tooltip="Includes stations in Hawaii and Alaska."
                        onChange={(value: string) =>
                          setFieldValue(getFullFieldName('isDeployed'), value)
                        }
                        options={{ true: 'Yes', false: 'No' }}
                      />
                    </Column>
                  )}
                  {/* Retain column width without militaryRank or isDeployed */}
                  {[
                    '',
                    'no_military_history',
                    'discharged_or_retired',
                  ].includes(militaryInputProps.value) && <Column />}
                </Row>
              )}
            </>
          )}
          {driverType === 'principalInsured' && askForCurrentPolicy && (
            <>
              <h4 className={styles.heading}>Current Insurance</h4>
              <Row>
                {!appConfig?.accessEnabled && (
                  <Column variant="third">
                    <FormInput
                      {...getInputProps('isCurrentlyInsured')}
                      variant={readOnly ? 'readOnly' : 'radioButtonGroup'}
                      data-testid="currently-insured"
                      label="Currently Insured?"
                      onChange={(value: string) => {
                        setFieldValue(
                          getFullFieldName('isCurrentlyInsured'),
                          value
                        );
                      }}
                      options={{ true: 'Yes', false: 'No' }}
                    />
                  </Column>
                )}
                {currentDriver.isCurrentlyInsured === 'true' ? (
                  <>
                    <Column variant="third">
                      <FormInput
                        {...getInputProps('currentInsuranceCarrierName')}
                        variant={readOnly ? 'readOnly' : 'combobox'}
                        data-testid="current-insurance-carrier"
                        label="Current insurance carrier"
                        onChange={(value: string) =>
                          setFieldValue(
                            getFullFieldName('currentInsuranceCarrierName'),
                            value
                          )
                        }
                        options={config.currentInsurance.carrierName}
                      />
                    </Column>
                    <Column variant="third">
                      <FormInput
                        {...getInputProps(
                          'currentInsuranceBiLevel',
                          currentInsuranceBiLevel !== 'unknown'
                        )}
                        variant={readOnly ? 'readOnly' : 'selectAutocomplete'}
                        data-testid="current-insurance-bi-level"
                        label="Bodily injury limits"
                        onChange={(value: string) =>
                          setFieldValue(
                            getFullFieldName('currentInsuranceBiLevel'),
                            value
                          )
                        }
                        options={config.currentInsurance.biLevel}
                      />
                    </Column>
                  </>
                ) : currentDriver.isCurrentlyInsured === 'false' ? (
                  <>
                    <Column variant="third">
                      <FormInput
                        {...getInputProps('currentInsuranceLapseDuration')}
                        variant={readOnly ? 'readOnly' : 'selectAutocomplete'}
                        data-testid="current-insurance-lapse-duration"
                        label="How long has driver been uninsured?"
                        onChange={(value: string) =>
                          setFieldValue(
                            getFullFieldName('currentInsuranceLapseDuration'),
                            value
                          )
                        }
                        options={config.currentInsurance.lapseDuration}
                      />
                    </Column>
                    <Column variant="third">
                      <FormInput
                        {...getInputProps('currentInsuranceLapseReason')}
                        variant={readOnly ? 'readOnly' : 'selectAutocomplete'}
                        data-testid="current-insurance-lapse-reason"
                        label="Reason for lapse"
                        onChange={(value: string) =>
                          setFieldValue(
                            getFullFieldName('currentInsuranceLapseReason'),
                            value
                          )
                        }
                        options={config.currentInsurance.lapseReason}
                      />
                    </Column>
                  </>
                ) : (
                  <Column variant="third" />
                )}
              </Row>
              {currentDriver.isCurrentlyInsured === 'false' &&
                currentDriver.currentInsuranceLapseReason === 'other' && (
                  <Row>
                    <Column variant="third">
                      <FormInput
                        {...getInputProps(
                          'currentInsuranceLapseReasonOther',
                          false
                        )}
                        variant={readOnly ? 'readOnly' : 'text'}
                        label="Other reason for lapse"
                        data-testid="reason-for-lapse-other"
                      />
                    </Column>
                    <Column variant="third" />
                  </Row>
                )}
            </>
          )}
        </>
      )}
      {isStateResidencyRequired &&
        !markedAsListedDriver &&
        currentDriver.excludeFromQuote !== 'true' && (
          <Row>
            <Column variant="third">
              <FormInput
                {...getInputProps('inStateResidencyDuration')}
                variant={readOnly ? 'readOnly' : 'radioButtonGroup'}
                data-testid="in-state-residency-duration"
                label={`In a typical year, does this driver reside in ${state} for at least 10 months? `}
                tooltip={
                  'If the driver is an active military member currently deployed (domestic or overseas) or on temporary duty assignment, select ‘Yes’.'
                }
                tooltipLabel={
                  <label className={styles.tooltipLabel}>Military</label>
                }
                tooltipClassName={styles.tooltipWrapper}
                onChange={(value: string) => {
                  setFieldValue(
                    getFullFieldName('inStateResidencyDuration'),
                    value
                  );
                  if (value === 'true') {
                    setFieldValue(getFullFieldName('outOfStateStudent'), '');
                    setFieldValue(
                      getFullFieldName('ownVehicleWhileOutOfStateStudent'),
                      ''
                    );
                    setFieldValue(
                      getFullFieldName('multipleHouseholdsMultipleStates'),
                      ''
                    );
                  }
                }}
                options={{ atLeast10Months: 'Yes', lessThan10Months: 'No' }}
              />
            </Column>
            <Column variant="third">
              <FormInput
                {...getInputProps(
                  'outOfStateStudent',
                  inStateResidencyDuration === 'lessThan10Months'
                )}
                variant={readOnly ? 'readOnly' : 'radioButtonGroup'}
                data-testid="out-of-state-student"
                label="Is driver a full time student away?"
                onChange={(value: string) => {
                  setFieldValue(getFullFieldName('outOfStateStudent'), value);
                  if (value === 'true') {
                    setFieldValue(
                      getFullFieldName('multipleHouseholdsMultipleStates'),
                      ''
                    );
                  } else if (value === 'false') {
                    setFieldValue(
                      getFullFieldName('ownVehicleWhileOutOfStateStudent'),
                      ''
                    );
                  }
                }}
                options={{ true: 'Yes', false: 'No' }}
                disabled={inStateResidencyDuration !== 'lessThan10Months'}
              />
            </Column>
            <Column variant="third">
              <FormInput
                {...getInputProps(
                  'ownVehicleWhileOutOfStateStudent',
                  inStateResidencyDuration === 'lessThan10Months' &&
                    outOfStateStudent === 'true'
                )}
                variant={readOnly ? 'readOnly' : 'radioButtonGroup'}
                data-testid="own-vehicle-while-out-of-state-student"
                label={`Is driver in possession of insured vehicle while attending school outside of ${state}?`}
                onChange={(value: string) =>
                  setFieldValue(
                    getFullFieldName('ownVehicleWhileOutOfStateStudent'),
                    value
                  )
                }
                options={{ true: 'Yes', false: 'No' }}
                disabled={
                  inStateResidencyDuration !== 'lessThan10Months' ||
                  outOfStateStudent !== 'true'
                }
              />
            </Column>
          </Row>
        )}
      {isStateResidencyRequired &&
        !markedAsListedDriver &&
        currentDriver.excludeFromQuote !== 'true' && (
          <Row>
            <Column variant="third">
              <FormInput
                {...getInputProps(
                  'multipleHouseholdsMultipleStates',
                  inStateResidencyDuration === 'lessThan10Months' &&
                    outOfStateStudent === 'false'
                )}
                variant={readOnly ? 'readOnly' : 'radioButtonGroup'}
                data-testid="multiple-households-multiple-states"
                label={`Is driver a permanent resident of ${state} with multiple households in multiple states?`}
                onChange={(value: string) => {
                  setFieldValue(
                    getFullFieldName('multipleHouseholdsMultipleStates'),
                    value
                  );
                }}
                options={{ true: 'Yes', false: 'No' }}
                disabled={
                  inStateResidencyDuration !== 'lessThan10Months' ||
                  outOfStateStudent !== 'false'
                }
              />
            </Column>
          </Row>
        )}
      {currentDriver.excludeFromQuote !== 'true' && (
        <>
          <ViolationList
            driverId={driverId}
            config={config}
            alwaysShowErrors={alwaysShowErrors}
          />
          <AccidentList
            driverId={driverId}
            config={config}
            alwaysShowErrors={alwaysShowErrors}
          />
        </>
      )}
    </>
  );
};
